import { useApi } from '@/api';
import {
  Banner,
  ExternalLinkIcon,
  KpiCard,
  LinkButton,
  Page,
  PageSkeleton
} from '@/components';
import UpgradeLogo from '@/components/icons/UpgradeLogo';
import { useSearchParams } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import { Box, CurrencyValue, Flex, Heading } from '@blockanalitica/ui';
import HistoricTabs from './HistoricTabs';
import MigrationBreakdown from './MigrationBreakdown';

export default function UpgradePage() {
  const [daysAgo] = useSearchParams('days_ago', '1');
  const [data, error] = useApi({
    url: '/migrations/migration/',
    queryParams: {
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Page
        title={
          <Flex gap="2xs" alignItems="center">
            <UpgradeLogo size="5" />
            <Heading size="1">MKR to SKY Upgrade</Heading>
          </Flex>
        }
        actions={
          <>
            <LinkButton
              to="https://app.sky.money/?widget=upgrade&source_token=MKR"
              target="_blank">
              <Flex
                gap="3xs"
                alignItems="center"
                style={{ whiteSpace: 'nowrap' }}>
                Upgrade MKR to SKY
                <ExternalLinkIcon size="1" />
              </Flex>
            </LinkButton>
          </>
        }>
        <Banner>
          <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
            Sky Ecosystem Governance has{' '}
            <a
              href="https://vote.makerdao.com/polling/QmTVd4iq"
              target="_blank"
              rel="noreferrer">
              voted to make SKY the sole governance token
            </a>{' '}
            of the Sky Protocol. MKR holders are encouraged to upgrade to SKY
            promptly to maintain the ability to participate in governance,
            maintain access to Staking Rewards and avoid the Delayed Upgrade
            Penalty.
            <br />
            For more information, please visit{' '}
            <a
              href="https://upgrademkrtosky.sky.money/"
              target="_blank"
              rel="noreferrer">
              the MKR to SKY Upgrade Hub.
            </a>
          </Box>
        </Banner>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            variant="mkr"
            title="Upgraded from MKR to SKY"
            data={data}
            field="migrated"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  iconSize: '4',
                  currency: tokensMapping('MKR')
                }
              }
            }}
            footer={
              <Flex gap="3xs" flexDirection="row" alignItems="baseline">
                <CurrencyValue
                  value={data.migrated * 24000}
                  options={{
                    currency: tokensMapping('SKY'),
                    currencySize: '0'
                  }}
                />
                <CurrencyValue
                  value={data.migrated_change * 24000}
                  options={{ trend: true }}
                />
              </Flex>
            }
          />
          <KpiCard
            variant="sky"
            title="% of MKR upgraded"
            data={data}
            field="percentage"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
        </Flex>
        <HistoricTabs />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Delayed Upgrade Penalty"
            data={data}
            field="penalty"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
            footer={
              <Flex gap="3xs" flexDirection="row" alignItems="baseline">
                The September 18th 2025 Spell will introduce penalty at 1%. The
                penalty will increase gradually by 1% points per 3 months after
                initiation.
              </Flex>
            }
          />
        </Flex>
        <MigrationBreakdown />
      </Page>
    );
  }

  return <PageSkeleton />;
}
